/**
 * S-27 › Notifications (V8 — ledger #75). The toggle list mirrors the
 * ©notifications opt-out table 1:1 (normative bijection); below it the
 * read-only "Always on" mandatory list with ©notifications.mandatory_note —
 * no toggles by design. Plus the Web Push opt-in (VAPID via /client-config).
 */
import { useEffect, useState } from "react";
import { copy } from "@humble/shared";
import { api, isApiError } from "../../lib/api.js";
import { Button, Card, Skeleton, toast } from "../../design/index.js";
import { rawApi } from "../safety/raw-api.js";
import "./notifications.css";

interface PrefRow {
  category: string;
  enabled: boolean;
  mandatory: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  match_classic: "Classic matches",
  match_reject: "Reject-Matches",
  message: "Messages",
  crush: "Crushes",
  sr_landed: "Super Reject landed",
  shield_block: "Shield blocks",
  streak: "Killstreak events",
  takeover: "Takeover offers",
  match_expiry: "Match expiry warnings",
  cooldown_return: "Cooldown over",
  roulette: "Roulette pit activity",
  pity: "Consolation boosts",
  call: "Calls",
  run_it_back: "Run It Back offers",
  product: "Humble news",
  moderation: "Moderation notices",
  verification: "Verification results",
  account: "Account & data-export notices",
};

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function NotificationSettingsScreen() {
  const [prefs, setPrefs] = useState<PrefRow[] | null>(null);
  const [note, setNote] = useState<string>(copy.notifications.mandatory_note);
  const [pushState, setPushState] = useState<"unknown" | "off" | "on" | "unsupported">("unknown");

  useEffect(() => {
    rawApi<{ categories: PrefRow[]; mandatory_note: string }>("GET", "/notifications/prefs")
      .then((r) => {
        setPrefs(r.categories);
        setNote(r.mandatory_note);
      })
      .catch(() => setPrefs([]));

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setPushState("unsupported");
      return;
    }
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setPushState(sub ? "on" : "off"))
      .catch(() => setPushState("off"));
  }, []);

  const flip = async (category: string, enabled: boolean) => {
    setPrefs((prev) => prev?.map((p) => (p.category === category ? { ...p, enabled } : p)) ?? null);
    try {
      await rawApi("PUT", "/notifications/prefs", { category, enabled });
    } catch (err) {
      toast(isApiError(err) ? err.message : "Couldn't save the toggle.", "error");
      setPrefs(
        (prev) => prev?.map((p) => (p.category === category ? { ...p, enabled: !enabled } : p)) ?? null,
      );
    }
  };

  const enablePush = async () => {
    try {
      const cfg = await api.get("/client-config");
      const vapid = (cfg as { vapid_public_key?: string | null }).vapid_public_key;
      if (!vapid) {
        toast("Push is not configured on this deployment.", "error");
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        toast("Push permission was not granted.", "info");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid) as unknown as BufferSource,
      });
      const json = sub.toJSON();
      await api.post("/push/subscriptions", {
        body: {
          endpoint: sub.endpoint,
          keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
        } as never,
      });
      setPushState("on");
      toast("Push notifications enabled.", "success");
    } catch (err) {
      toast(isApiError(err) ? err.message : "Push subscription failed.", "error");
    }
  };

  const disablePush = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete("/push/subscriptions", { body: { endpoint: sub.endpoint } as never });
        await sub.unsubscribe();
      }
      setPushState("off");
    } catch {
      toast("Couldn't unsubscribe — try again.", "error");
    }
  };

  const optOut = prefs?.filter((p) => !p.mandatory) ?? [];
  const mandatory = prefs?.filter((p) => p.mandatory) ?? [];

  return (
    <div className="notif">
      <header className="notif__head">
        <p className="h-caps notif__kicker">Signal control</p>
        <h1 className="type-display-3">Notifications</h1>
        <p className="type-editorial notif__lede">{copy.notifications.lede}</p>
      </header>

      <Card padding="md" style={{ marginBottom: 12 }}>
        <h2 className="notif__h2" style={{ marginBottom: 8 }}>
          Push delivery
        </h2>
        {pushState === "unsupported" ? (
          <p className="text-dim" style={{ margin: 0 }}>
            This browser does not support Web Push.
          </p>
        ) : pushState === "on" ? (
          <Button variant="ghost" onClick={() => void disablePush()}>
            Disable push on this device
          </Button>
        ) : (
          <Button variant="primary" onClick={() => void enablePush()} disabled={pushState === "unknown"}>
            Enable push on this device
          </Button>
        )}
      </Card>

      {prefs === null ? (
        <Skeleton style={{ height: 200 }} radius={20} />
      ) : (
        <>
          <Card padding="md" style={{ marginBottom: 12 }}>
            {optOut.map((p) => (
              <label key={p.category} className="notif__row">
                <span className="notif__row-label">
                  {CATEGORY_LABELS[p.category] ?? p.category}
                  {p.category === "call" && (
                    <span className="notif__row-note">{copy.notifications.call_optout_note}</span>
                  )}
                </span>
                <input
                  type="checkbox"
                  className="notif__switch"
                  checked={p.enabled}
                  onChange={(e) => void flip(p.category, e.target.checked)}
                />
              </label>
            ))}
          </Card>

          <Card padding="md">
            <h2 className="notif__h2" style={{ marginBottom: 8 }}>
              Always on
            </h2>
            {/* mandatory list — protective notices, plain tone by design */}
            <p className="text-dim" style={{ fontSize: "0.88rem" }}>
              {note}
            </p>
            <ul className="notif__mandatory">
              {mandatory.map((p) => (
                <li key={p.category}>{CATEGORY_LABELS[p.category] ?? p.category}</li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </div>
  );
}
